import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import {
  CreationDateRenderer,
  OwnerRenderer,
  TenantContextMenuRenderer,
  TenantSubscriptionEndRenderer,
  TenantSubscriptionStartRenderer,
  TenantNameRenderer,
  TenantPlanRenderer,
  TenantSubscriptionStatusRenderer,
  TermsRenderer,
} from '@/backoffice/features/tenants/components/tenants-list/tenants-list.renderer';
import { TTransformedTenant } from '@/backoffice/features/tenants/hooks/use-tenants-transformer/use-tenants-transformer.types';
import {
  compareSubscriptionStatus,
  getSubscriptionStatus,
} from '@/backoffice/features/tenants/utils/subscription-status';
import { CONTEXT_MENU_CELL_ID } from '@/shared/components/ui/data-table/components/ag-grid-table-core/ag-grid-table-core.constants';
import { getDateComparator } from '@/shared/components/ui/data-table/data-table.comparator';
import { TGroupListActionsRenderer } from '@/shared/features/groups/context/groups-config-context.types';
import { useGroupColumn } from '@/shared/features/groups/hooks/use-group-column/use-group-column';
import { getFullname } from '@/shared/utils/format';

export const useTenantsListDefinitions = (options?: {
  hideGroupColumn?: boolean;
  actionsRenderer?: TGroupListActionsRenderer;
}) => {
  const { t } = useTranslation();
  const groupColumn = useGroupColumn();
  const { hideGroupColumn, actionsRenderer } = options ?? {};

  const columnDefs: ColDef[] = [
    {
      field: 'companyName',
      headerName: t('settings:manage_company.company_name'),
      minWidth: 240,
      sortable: true,
      cellRenderer: TenantNameRenderer,
    },
    {
      field: 'tenantId',
      headerName: t('common:id'),
      minWidth: 100,
      sortable: true,
    },
    {
      field: 'country',
      headerName: t('onboarding:country'),
      minWidth: 150,
      sortable: true,
    },
    {
      field: 'industry',
      headerName: t('onboarding:industry'),
      minWidth: 150,
      sortable: true,
    },
    {
      field: 'owner',
      headerName: t('common:owner'),
      minWidth: 150,
      sortable: true,
      cellRenderer: OwnerRenderer,
      valueGetter: ({ data }: ValueGetterParams<TTransformedTenant>) =>
        getFullname(data?.owner),
    },
    {
      field: 'tcAccepted',
      headerName: t('backoffice:terms_accepted'),
      minWidth: 150,
      sortable: true,
      cellRenderer: TermsRenderer,
    },
    {
      colId: 'plan',
      headerName: t('billing:plan'),
      minWidth: 150,
      sortable: true,
      cellRenderer: TenantPlanRenderer,
      valueGetter: ({ data }: ValueGetterParams<TTransformedTenant>) =>
        data?.subscription?.tier,
    },
    {
      colId: 'subscriptionStatus',
      headerName: t('billing:subscription_status'),
      minWidth: 210,
      sortable: true,
      cellRenderer: TenantSubscriptionStatusRenderer,
      valueGetter: ({ data }: ValueGetterParams<TTransformedTenant>) =>
        data?.subscription
          ? getSubscriptionStatus(data.subscription)
          : undefined,
      comparator: compareSubscriptionStatus,
    },
    {
      colId: 'subscriptionStart',
      headerName: t('backoffice:subscription_start'),
      minWidth: 200,
      sortable: true,
      cellRenderer: TenantSubscriptionStartRenderer,
      valueGetter: ({ data }: ValueGetterParams<TTransformedTenant>) =>
        data?.subscription?.term.startTs,
      comparator: getDateComparator(),
    },
    {
      colId: 'subscriptionEnd',
      headerName: t('backoffice:subscription_end'),
      minWidth: 200,
      sortable: true,
      cellRenderer: TenantSubscriptionEndRenderer,
      valueGetter: ({ data }: ValueGetterParams<TTransformedTenant>) =>
        data?.subscription?.term.cancellationTs ??
        data?.subscription?.term.expirationTs,
      comparator: getDateComparator(),
    },
    {
      field: 'creationDate',
      headerName: t('common:creation_date'),
      minWidth: 150,
      sortable: true,
      cellRenderer: CreationDateRenderer,
      comparator: getDateComparator(),
    },
    ...(hideGroupColumn ? [] : [groupColumn]),
    {
      field: CONTEXT_MENU_CELL_ID,
      headerName: '',
      minWidth: 150,
      maxWidth: 150,
      cellRenderer: actionsRenderer ?? TenantContextMenuRenderer,
      sortable: false,
      resizable: false,
    },
  ];

  return columnDefs;
};
